
import { Component, Mixins } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { OrderModule, CompanyModule } from "@/store/modules/index";
import notifications from "../../../lib/notifications";
import mixin from "./mixin";
import { IOrderResource } from "@/store/types";
import * as modals from "@/lib/modals";
import { formatPhoneNumber } from "@/utils";

const orderModule = getModule(OrderModule);
const companyModule = getModule(CompanyModule);

interface DefaultContact {
  id: string;
  name: string;
  phoneNumber: string;
  phoneType: string;
  textMessageOptIn: boolean;
  emailAddress: string;
  emailOptIn: boolean;
  isDefault: boolean;
  isActive: boolean;
  companyId: number | string;
  promptAccepted: boolean;
  currentOrderHeaderNumber: number;
  copyContact: boolean;
}

@Component({
  name: modals.consigneeContactEntryModal
})
export default class ConsigneeContactEntryModal extends Mixins(mixin) {
  isSaving: boolean = false;
  defaultContact: DefaultContact = {
    id: null,
    name: null,
    phoneNumber: null,
    phoneType: "B",
    textMessageOptIn: false,
    emailAddress: null,
    emailOptIn: false,
    isDefault: false,
    isActive: true,
    companyId: null,
    promptAccepted: false,
    currentOrderHeaderNumber: null,
    copyContact: false
  };
  contact: DefaultContact = { ...this.defaultContact };
  alertMessage: string = "";
  showAlert: boolean = false;
  showLinkedOrdersAlert: boolean = false;
  creatingNewContact: boolean = false;


  get isExisting() {
    return this.contact.id !== null;
  }

  get currentOrder(): IOrderResource {
    return orderModule.currentOrder;
  }  
  
  get shipsWithOrders(): Array<{ number: number }> {
    return orderModule.currentOrderShipsWithOrders;
  }

  formattedPhoneNumber(phoneNumber) {
    return phoneNumber ? formatPhoneNumber(phoneNumber) : '';
  }

  load() {
    if (this.modalData) {
      const existingContact = this.modalData;
      this.contact.id = existingContact.id;
      this.contact.name = existingContact.name;
      this.contact.phoneNumber = this.formattedPhoneNumber(existingContact.phoneNumber);
      this.contact.phoneType = existingContact.phoneType;
      this.contact.textMessageOptIn = existingContact.textMessageOptIn;
      this.contact.emailOptIn = existingContact.emailOptIn;
      this.contact.emailAddress = existingContact.emailAddress;
      this.contact.isActive = true;
      this.contact.promptAccepted = false;
      this.contact.currentOrderHeaderNumber = existingContact.currentOrderHeaderNumber;
    } else {
      this.contact = { ...this.defaultContact };
    }
  }

  async saveModal() {
    this.contact.companyId = orderModule.currentOrder.contactCompanyId;
    this.isSaving = true;

    if (this.isExisting) {
      await this.updateExistingContact();
    } else {
      this.creatingNewContact = true;
      await this.createNewContact();
    } 
    this.isSaving = false;

    // Ask if should populate to linked orders
    if(this.shipsWithOrders && this.shipsWithOrders.length > 0) {
      this.showLinkedOrdersAlert = true;
    } 
    if(this.showAlert == false && this.showLinkedOrdersAlert == false) {
      this.hideModal();
    }
  }

  acceptContactChanges() {
    this.contact.promptAccepted = true;
    this.updateExistingContact();
    this.showLinkedOrdersAlert = false;
    this.hideModal();
  }

  applyContactChanges() {
    this.shipsWithOrders.forEach(async (order) => {
      if(this.currentOrder.headerNumber !== order.number) {
        try {
          if (!this.creatingNewContact) {
            this.contact.currentOrderHeaderNumber = order.number;
            this.contact.copyContact = true;
            const contactPayload = JSON.parse(JSON.stringify(this.contact));
            contactPayload.phoneNumber = this.contact.phoneNumber.replace(/\D/g, '');
            await companyModule.updateCompanyContact(contactPayload);
          } else {
            await orderModule.addConsigneeContact({
              orderNumber: order.number,
              contactId: this.contact.id,
              copyContact: true
            });
          }
        } catch (ex) {
          notifications.error("Failed to update linked order", `Failed to update order ${order.number}`, ex);
        }
      }
    });
    this.contact.promptAccepted = true;
    this.updateExistingContact();
    this.showLinkedOrdersAlert = false;
    this.hideModal();
  }

  dismissModal() {
    this.showAlert = false;
    this.hideModal();
  }

  async updateExistingContact() {
    this.contact.currentOrderHeaderNumber = this.currentOrder.headerNumber;
    try {
      const contactPayload = JSON.parse(JSON.stringify(this.contact));
      contactPayload.phoneNumber = this.contact.phoneNumber.replace(/\D/g, '');
      const response = await companyModule.updateCompanyContact(contactPayload);
      if (response.requiresPrompt) {
        this.alertMessage = response;
        this.showAlert = true;
      } else {
        notifications.success("Contact Updated", "Contact successfully updated.");
        this.showAlert = false;
      }
    } catch (ex) {
      notifications.error("Contact Not Updated", "Failed to update contact", ex);
      this.hideModal();
      this.showAlert = false;
    }
  }

  async createNewContact() {
    let contactId = null;
    try {
      const contactPayload = JSON.parse(JSON.stringify(this.contact));
      contactPayload.phoneNumber = this.contact.phoneNumber.replace(/\D/g, '')
      let response = await companyModule.createCompanyContact(contactPayload);
      contactId = response.data.id;

      if (contactId)
        await orderModule.addConsigneeContact({
          orderNumber: this.currentOrder.number,
          contactId: contactId
        });
        this.contact.id = contactId;
      notifications.success("Contact added", "Contact successfully added to order.");
    } catch (ex) {
      if (contactId) {
        notifications.error("Company contact failed", "Failed to create new contact.", ex);
      } else {
        notifications.error("Company contact failed", "Failed to add new contact to order.", ex);
      }
    }
    this.showAlert = false;
  }

  acceptNumber(e) {
    const x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    this.$set(this.contact, 'phoneNumber', !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : ''));
  }
}
